import React, { useState } from 'react';
import { Calendar, Clock, Send, CheckCircle, Building, Scale, Anchor, Home } from 'lucide-react';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

const consultationTypes = [
  { value: 'corporate', label: 'Corporate & Business Law', icon: Building },
  { value: 'civil', label: 'Civil Litigation', icon: Scale },
  { value: 'maritime', label: 'Maritime Law', icon: Anchor },
  { value: 'real-estate', label: 'Real Estate Law', icon: Home }
];

const timeSlots = [
  '9:00 AM', '10:00 AM', '11:00 AM', 
  '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM'
];

export const BookAppointmentPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    consultation_type: '',
    preferred_date: null,
    preferred_time: '',
    details: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDateSelect = (date) => {
    setFormData(prev => ({ ...prev, preferred_date: date }));
    setCalendarOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.preferred_date || !formData.preferred_time) {
      toast.error('Please select a preferred date and time');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const submitData = {
        ...formData,
        preferred_date: format(formData.preferred_date, 'yyyy-MM-dd')
      };
      
      await axios.post(`${API}/bookings`, submitData);
      setSubmitted(true);
      toast.success('Appointment request submitted!');
    } catch (error) {
      console.error('Error submitting booking:', error);
      toast.error('Failed to submit booking. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-8 md:p-12 lg:p-16">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <div className="text-center mb-12" data-testid="book-header">
        <p className="font-body text-xs uppercase tracking-[0.2em] text-[#C9A86C] mb-2">Schedule Your Visit</p>
        <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#1a1412] mb-4">
          Book an Appointment
        </h1>
        <p className="font-body text-[#7a7065] max-w-2xl mx-auto">
          Schedule a consultation with Devon Brooks to discuss your legal needs. 
          We'll confirm your appointment within 24 hours.
        </p>
      </div>

      {submitted ? (
        <div className="max-w-xl mx-auto bg-white p-10 rounded-xl shadow-lg text-center" data-testid="booking-success">
          <CheckCircle className="mx-auto text-[#C9A86C] mb-6" size={64} />
          <h2 className="font-heading text-3xl font-semibold text-[#1a1412] mb-4">
            Booking Submitted
          </h2>
          <p className="font-body text-[#7a7065] mb-8">
            Thank you for scheduling a consultation. We'll review your request and confirm 
            your appointment within 24 hours.
          </p>
          <div className="bg-[#f5f0e8] rounded-lg p-6 mb-8 text-left">
            <div className="space-y-3">
              <p className="font-body text-sm text-[#5a4a3a]">
                <span className="font-semibold">Date:</span> {formData.preferred_date && format(formData.preferred_date, 'MMMM d, yyyy')}
              </p>
              <p className="font-body text-sm text-[#5a4a3a]">
                <span className="font-semibold">Time:</span> {formData.preferred_time}
              </p>
              <p className="font-body text-sm text-[#5a4a3a]">
                <span className="font-semibold">Service:</span> {consultationTypes.find(t => t.value === formData.consultation_type)?.label}
              </p>
            </div>
          </div>
          <button 
            onClick={() => {
              setSubmitted(false);
              setFormData({
                name: '', email: '', phone: '',
                consultation_type: '', preferred_date: null,
                preferred_time: '', details: ''
              });
            }}
            className="btn-secondary"
            data-testid="book-another-btn"
          >
            Book Another Appointment
          </button>
        </div>
      ) : (
        <div className="max-w-3xl mx-auto">
          <form onSubmit={handleSubmit} className="bg-white p-8 md:p-10 rounded-xl shadow-lg border border-[#C9A86C]/10">
            {/* Personal Information */}
            <div className="mb-10">
              <h3 className="font-heading text-xl font-semibold text-[#1a1412] mb-6 pb-3 border-b border-[#C9A86C]/20 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-[#C9A86C] text-white flex items-center justify-center text-sm font-body">1</span>
                Personal Information
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="font-body text-xs uppercase tracking-wider text-[#7a7065] mb-3 block font-medium">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input rounded-lg"
                    placeholder="Enter your full name"
                    data-testid="book-name-input"
                  />
                </div>
                <div>
                  <label className="font-body text-xs uppercase tracking-wider text-[#7a7065] mb-3 block font-medium">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input rounded-lg"
                    placeholder="Enter your email"
                    data-testid="book-email-input"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="font-body text-xs uppercase tracking-wider text-[#7a7065] mb-3 block font-medium">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="form-input rounded-lg"
                    placeholder="Enter your phone number"
                    data-testid="book-phone-input"
                  />
                </div>
              </div>
            </div>

            {/* Consultation Type */}
            <div className="mb-10">
              <h3 className="font-heading text-xl font-semibold text-[#1a1412] mb-6 pb-3 border-b border-[#C9A86C]/20 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-[#C9A86C] text-white flex items-center justify-center text-sm font-body">2</span>
                Select Service
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {consultationTypes.map((type) => (
                  <label
                    key={type.value}
                    className={`
                      flex flex-col items-center p-5 rounded-xl border-2 cursor-pointer transition-all
                      ${formData.consultation_type === type.value 
                        ? 'border-[#C9A86C] bg-[#C9A86C]/10 shadow-md' 
                        : 'border-[#e8e0d4] hover:border-[#C9A86C]/50 hover:bg-[#f5f0e8]'
                      }
                    `}
                    data-testid={`book-type-${type.value}`}
                  >
                    <input
                      type="radio"
                      name="consultation_type"
                      value={type.value}
                      checked={formData.consultation_type === type.value}
                      onChange={handleChange}
                      className="sr-only"
                      required
                    />
                    <type.icon 
                      className={`mb-3 transition-colors ${formData.consultation_type === type.value ? 'text-[#C9A86C]' : 'text-[#9a8a7a]'}`} 
                      size={28} 
                    />
                    <span className="font-body text-xs text-center text-[#1a1412] font-medium">{type.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Date & Time */}
            <div className="mb-10">
              <h3 className="font-heading text-xl font-semibold text-[#1a1412] mb-6 pb-3 border-b border-[#C9A86C]/20 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-[#C9A86C] text-white flex items-center justify-center text-sm font-body">3</span>
                Preferred Date & Time
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="font-body text-xs uppercase tracking-wider text-[#7a7065] mb-3 flex items-center gap-2 font-medium">
                    <Calendar size={14} className="text-[#C9A86C]" />
                    Select Date
                  </label>
                  <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="form-input rounded-lg text-left flex items-center justify-between cursor-pointer"
                        data-testid="book-date-trigger"
                      >
                        {formData.preferred_date 
                          ? <span className="text-[#1a1412]">{format(formData.preferred_date, 'MMMM d, yyyy')}</span>
                          : <span className="text-[#9a8a7a]">Choose a date</span>
                        }
                        <Calendar size={18} className="text-[#C9A86C]" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-white" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={formData.preferred_date}
                        onSelect={handleDateSelect}
                        disabled={(date) => 
                          date < new Date() || 
                          date.getDay() === 0 || 
                          date.getDay() === 6
                        }
                        initialFocus
                        data-testid="book-calendar"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <label className="font-body text-xs uppercase tracking-wider text-[#7a7065] mb-3 flex items-center gap-2 font-medium">
                    <Clock size={14} className="text-[#C9A86C]" />
                    Select Time
                  </label>
                  <select
                    name="preferred_time"
                    value={formData.preferred_time}
                    onChange={handleChange}
                    required
                    className="form-input rounded-lg cursor-pointer"
                    data-testid="book-time-select"
                  >
                    <option value="">Choose a time slot</option>
                    {timeSlots.map((slot) => (
                      <option key={slot} value={slot}>{slot}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Details */}
            <div className="mb-10">
              <h3 className="font-heading text-xl font-semibold text-[#1a1412] mb-6 pb-3 border-b border-[#C9A86C]/20 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-[#C9A86C] text-white flex items-center justify-center text-sm font-body">4</span>
                Additional Details
              </h3>
              <label className="font-body text-xs uppercase tracking-wider text-[#7a7065] mb-3 block font-medium">
                Brief Description of Your Legal Matter (Optional)
              </label>
              <textarea
                name="details"
                value={formData.details}
                onChange={handleChange}
                rows={4}
                className="form-input rounded-lg resize-none"
                placeholder="Please provide a brief overview of your legal needs..."
                data-testid="book-details-input"
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary w-full flex items-center justify-center gap-3 py-4 rounded-lg"
              data-testid="book-submit-btn"
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Processing...
                </>
              ) : (
                <>
                  <Send size={18} />
                  Request Appointment
                </>
              )}
            </button>

            <p className="font-body text-xs text-[#9a8a7a] text-center mt-6">
              We will confirm your appointment within 24 hours via email or phone.
            </p>
          </form>
        </div>
      )}
    </div>
  );
};

export default BookAppointmentPage;
